import React, { useState, useEffect } from 'react';
import { Mic, Search, Clock, User, FileText, ExternalLink, Download, RefreshCw } from 'lucide-react';
import axios from 'axios';

const OtterAI = () => {
  const [transcripts, setTranscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingDetail, setViewingDetail] = useState(false);

  // Use same-origin to avoid CORS issues in all environments
  const backendUrl = typeof window !== 'undefined' ? window.location.origin : '';

  useEffect(() => {
    loadTranscripts();
  }, []);

  const loadTranscripts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/otter/transcripts`);
      setTranscripts(response.data.transcripts || []);
    } catch (error) {
      console.error('Error loading transcripts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTranscriptDetail = async (speechId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/otter/transcripts/${speechId}`);
      setSelectedTranscript(response.data);
      setViewingDetail(true);
    } catch (error) {
      console.error('Error loading transcript detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredTranscripts = transcripts.filter(t => 
    t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.summary && t.summary.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (viewingDetail && selectedTranscript) {
    return (
      <div className="min-h-screen bg-gradient-to-br bg-transparent p-4 md:p-6">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => {
              setViewingDetail(false);
              setSelectedTranscript(null);
            }}
            className="mb-4 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            ← Back to Transcripts
          </button>

          {/* Transcript Detail Card */}
          <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">{selectedTranscript.title}</h1>
                <div className="flex items-center gap-4 text-gray-400 text-sm">
                  <span className="flex items-center gap-1">
                    <Clock size={16} />
                    {formatDate(selectedTranscript.created_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Mic size={16} />
                    {formatDuration(selectedTranscript.duration)}
                  </span>
                  {selectedTranscript.speakers && selectedTranscript.speakers.length > 0 && (
                    <span className="flex items-center gap-1">
                      <User size={16} />
                      {selectedTranscript.speakers.length} speaker(s)
                    </span>
                  )}
                </div>
              </div>
              <a
                href={`https://otter.ai/u/${selectedTranscript.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-gradient-to-r bg-white hover:bg-gray-100 text-black rounded-lg transition-colors flex items-center gap-2"
              >
                Open in Otter.ai
                <ExternalLink size={16} />
              </a>
            </div>

            {/* Summary */}
            {selectedTranscript.summary && (
              <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-2">Summary</h3>
                <p className="text-gray-300">{selectedTranscript.summary}</p>
              </div>
            )}

            {/* Full Transcript */}
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 max-h-[600px] overflow-y-auto">
              <h3 className="text-lg font-semibold text-white mb-4">Full Transcript</h3>
              <div className="text-gray-300 whitespace-pre-wrap">
                {selectedTranscript.transcript || 'No transcript text available.'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br bg-transparent p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <Mic className="text-white" size={32} />
              Otter.ai Transcripts
            </h1>
            <p className="text-gray-400">Your meeting transcriptions and recordings</p>
          </div>
          <button
            onClick={loadTranscripts}
            disabled={loading}
            className="px-4 py-2 bg-gradient-to-r bg-white hover:bg-gray-100 text-black rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Search transcripts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/10 border border-white/60 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && transcripts.length === 0 && (
          <div className="text-center py-20">
            <Mic className="mx-auto mb-4 text-gray-600" size={64} />
            <h3 className="text-xl font-semibold text-white mb-2">No transcripts found</h3>
            <p className="text-gray-400">Your Otter.ai recordings will appear here</p>
          </div>
        )}

        {/* Transcripts Grid */}
        {!loading && filteredTranscripts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTranscripts.map((transcript) => (
              <div
                key={transcript.id}
                onClick={() => loadTranscriptDetail(transcript.id)}
                className="bg-transparent border border-white/60 rounded-xl shadow-2xl shadow-[#C0C0C0]/10 p-5 hover:border-white/60 transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="bg-white/20 w-10 h-10 rounded-lg flex items-center justify-center">
                    <FileText className="text-white" size={20} />
                  </div>
                  <span className="text-xs text-gray-400">
                    {formatDuration(transcript.duration)}
                  </span>
                </div>
                
                <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">
                  {transcript.title || 'Untitled Recording'}
                </h3>
                
                {transcript.summary && (
                  <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                    {transcript.summary}
                  </p>
                )}
                
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {formatDate(transcript.created_at)}
                  </span>
                  {transcript.speaker_count > 0 && (
                    <span className="flex items-center gap-1">
                      <User size={14} />
                      {transcript.speaker_count}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Search Results */}
        {!loading && transcripts.length > 0 && filteredTranscripts.length === 0 && (
          <div className="text-center py-20">
            <Search className="mx-auto mb-4 text-gray-600" size={64} />
            <h3 className="text-xl font-semibold text-white mb-2">No results found</h3>
            <p className="text-gray-400">Try a different search term</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OtterAI;
